'use client';

import React, { useState } from 'react';
import { MarkdownRenderer } from '@/components/markdown/MarkdownRenderer';
import { 
  DocumentTextIcon,
  CheckIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import { ArticlePlanningData } from './ArticlePlanningForm';

export interface ArticleOutlineSection {
  heading: string;
  level: number;
  content?: string;
}

export interface AIGeneratedArticle {
  titles: string[];
  selectedTitle: string;
  outline?: ArticleOutlineSection[];
  introduction?: string;
  conclusion?: string;
  content?: string; // For the new direct-to-draft flow
  tags?: string[];
}

interface AIArticleSuggestionsProps {
  planningData: ArticlePlanningData;
  generatedArticle: AIGeneratedArticle;
  isLoading: boolean;
  onTitleSelect: (title: string) => void;
  onOutlineEdit: (outline: AIGeneratedArticle['outline']) => void;
  onRegenerateRequest: () => void;
  onAccept: (article: AIGeneratedArticle) => void;
  onBack: () => void;
}

export default function AIArticleSuggestions({
  planningData,
  generatedArticle,
  isLoading,
  onTitleSelect,
  onOutlineEdit,
  onRegenerateRequest,
  onAccept,
  onBack
}: AIArticleSuggestionsProps) {
  const [editingOutline, setEditingOutline] = useState(false);
  const [workingOutline, setWorkingOutline] = useState<ArticleOutlineSection[]>(generatedArticle.outline || []);

  const fullArticleMarkdown = (generatedArticle.outline || []).map(section => `${'#'.repeat(section.level)} ${section.heading}\n\n${section.content || ''}`).join('\n\n');
  
  const handleOutlineChange = (index: number, value: string) => {
    const newOutline = [...workingOutline];
    newOutline[index] = { ...newOutline[index], heading: value };
    setWorkingOutline(newOutline);
  };
  
  const handleAddSection = (afterIndex: number) => {
    const newOutline = [...workingOutline];
    const level = newOutline[afterIndex]?.level || 2;
    newOutline.splice(afterIndex + 1, 0, {
      heading: 'New Section',
      level: level
    });
    setWorkingOutline(newOutline);
  };
  
  const handleRemoveSection = (index: number) => {
    const newOutline = [...workingOutline];
    newOutline.splice(index, 1);
    setWorkingOutline(newOutline);
  };
  
  const saveOutlineChanges = () => {
    onOutlineEdit(workingOutline);
    setEditingOutline(false);
  };
  
  const cancelOutlineChanges = () => {
    setWorkingOutline(generatedArticle.outline || []);
    setEditingOutline(false);
  };

  return (
    <div className="bg-white shadow-md rounded-lg p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">AI-Generated Article Suggestions</h2>
        <p className="text-gray-600">
          Review and customize the AI-generated article structure before proceeding
        </p>
      </div>

        <div className="space-y-8">
          {/* Title suggestions */}
          <div className="border-b border-gray-200 pb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Suggested Titles</h3>
            <div className="space-y-3">
              {generatedArticle.titles.map((title, index) => (
                <div 
                  key={index}
                  className={`flex items-center p-3 rounded-md cursor-pointer ${
                    title === generatedArticle.selectedTitle 
                      ? 'bg-blue-50 border border-blue-200' 
                      : 'hover:bg-gray-50 border border-gray-200'
                  }`}
                  onClick={() => onTitleSelect(title)}
                >
                  <DocumentTextIcon className="h-5 w-5 text-gray-500 mr-3" />
                  <span className="flex-grow">{title}</span>
                  {title === generatedArticle.selectedTitle && (
                    <CheckIcon className="h-5 w-5 text-blue-500" />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Article outline */}
          {/* Full Article Preview */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Article Draft Preview</h3>
            <div className="bg-gray-50 p-6 rounded-md border border-gray-200">
              <MarkdownRenderer content={fullArticleMarkdown} />
            </div>
          </div>

        </div>
    </div>
  );
}
